import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  CircularProgress,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  PhotoLibrary as PhotoIcon
} from '@mui/icons-material';
import axios from 'axios';

const PEXELS_API_KEY = process.env.REACT_APP_PEXELS_API_KEY;

const PexelsSearch = ({ open, onClose, onSelectImage }) => {
  const [query, setQuery] = useState('');
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedImage, setSelectedImage] = useState(null);

  // Popular search categories
  const categories = [
    'Nature', 'Business', 'Abstract', 'Technology', 
    'People', 'Travel', 'Food', 'City', 'Animals'
  ];

  const searchPexels = async (searchQuery, pageNum = 1) => {
    if (!searchQuery) searchQuery = 'people photography'; // Default search for photos
    
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.get(`https://api.pexels.com/v1/search`, {
        params: {
          query: searchQuery,
          per_page: 20,
          page: pageNum
        },
        headers: {
          Authorization: PEXELS_API_KEY
        }
      });
      
      setImages(response.data.photos);
      setTotalPages(Math.ceil(response.data.total_results / 20));
    } catch (err) {
      console.error('Error fetching Pexels images:', err);
      setError('Failed to fetch images. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    searchPexels(query);
  };

  const handleCategoryClick = (category) => {
    setQuery(category);
    setPage(1);
    searchPexels(category);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
    searchPexels(query || 'people photography', value);
  };

  const handleImageSelect = (image) => {
    setSelectedImage(image);
  };

  const handleConfirmSelection = () => {
    if (selectedImage) {
      onSelectImage(selectedImage);
      onClose();
    }
  };

  // Load photos on initial render
  useEffect(() => {
    if (open) {
      searchPexels('people photography');
    }
  }, [open]);

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Stock Photos from Pexels</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        <Box sx={{ mb: 3 }}>
          <form onSubmit={handleSearch}>
            <TextField
              fullWidth
              placeholder="Search for photos..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton type="submit" edge="end">
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />
          </form>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {categories.map((category) => (
              <Button 
                key={category}
                size="small"
                variant="outlined"
                onClick={() => handleCategoryClick(category)}
                sx={{ textTransform: 'none' }}
              >
                {category}
              </Button>
            ))}
          </Box>
        </Box>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error" align="center" sx={{ p: 2 }}>
            {error}
          </Typography>
        ) : (
          <>
            <Grid container spacing={2}>
              {images.map((image) => (
                <Grid item xs={6} sm={4} md={3} key={image.id}>
                  <Box
                    sx={{
                      position: 'relative',
                      borderRadius: 1,
                      overflow: 'hidden',
                      cursor: 'pointer',
                      border: selectedImage?.id === image.id ? '3px solid #4299e1' : '3px solid transparent',
                      '&:hover': {
                        opacity: 0.9,
                      },
                      aspectRatio: '16/9',
                    }}
                    onClick={() => handleImageSelect(image)}
                  >
                    <img
                      src={image.src.medium}
                      alt={image.photographer}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
            
            {images.length > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination 
                  count={totalPages} 
                  page={page} 
                  onChange={handlePageChange} 
                  color="primary" 
                />
              </Box>
            )}
            
            {images.length === 0 && !loading && (
              <Box sx={{ textAlign: 'center', p: 4 }}>
                <PhotoIcon sx={{ fontSize: 60, color: 'text.secondary', opacity: 0.5 }} />
                <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                  No images found. Try a different search term.
                </Typography>
              </Box>
            )}
          </>
        )}
        
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Images provided by <a href="https://www.pexels.com" target="_blank" rel="noopener noreferrer">Pexels</a>
          </Typography>
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleConfirmSelection} 
          variant="contained" 
          disabled={!selectedImage}
        >
          Add Selected Image
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PexelsSearch; 